import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'group_checkout_started';
const STORE_STORAGE_KEY = 'checkout_started';

export function markGroupCheckoutStarted(groupId: number | string) {
  localStorage.setItem(STORAGE_KEY, String(groupId));
}

export function markStoreCheckoutStarted() {
  localStorage.setItem(STORE_STORAGE_KEY, 'true');
}

/**
 * Poll for ~12s when the stored checkout marker equals `expected`, so a
 * webhook-created subscription shows up without a manual refresh.
 */
function useCheckoutRefresh(
  storageKey: string,
  expected: string | undefined,
  refetchAll: () => Promise<unknown>,
) {
  const [isPolling, setIsPolling] = useState(false);
  const refetchRef = useRef(refetchAll);
  refetchRef.current = refetchAll;

  useEffect(() => {
    if (!expected) return;
    if (localStorage.getItem(storageKey) !== expected) return;

    setIsPolling(true);
    const startTime = Date.now();
    const interval = setInterval(async () => {
      await refetchRef.current();
      if (Date.now() - startTime > 12000) {
        clearInterval(interval);
        setIsPolling(false);
        localStorage.removeItem(storageKey);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [storageKey, expected]);

  return isPolling;
}

/**
 * After returning from a group Stripe Checkout (success_url is
 * /billing/group/:groupId), poll the group-scoped queries for ~12s.
 * Only polls when the stored checkout marker matches this exact group.
 */
export function useGroupCheckoutRefresh(
  groupId: number | string | undefined,
  refetchAll: () => Promise<unknown>,
) {
  return useCheckoutRefresh(
    STORAGE_KEY,
    groupId ? String(groupId) : undefined,
    refetchAll,
  );
}

/** Store-scope twin of `useGroupCheckoutRefresh`; `enabled` gates scope. */
export function useStoreCheckoutRefresh(
  enabled: boolean,
  refetchAll: () => Promise<unknown>,
) {
  return useCheckoutRefresh(
    STORE_STORAGE_KEY,
    enabled ? 'true' : undefined,
    refetchAll,
  );
}
